import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useIntl } from 'react-intl';

import { usePublish } from '@atlaskit/rovo-triggers/main';
import { useRovoExperienceAPIStore } from '@atlassian/rovo-experience-api/rovo-experience-api-store';

import { CHAT_OPEN_SOURCE, EXPERIMENT_NAME, EXPERIMENT_PARAM } from '../../common/constants';
import { expVal } from '../../common/exp-val';
import { type RovoReintroductionLauncherProps } from '../../types';
// eslint-disable-next-line @atlassian/tangerine/import/no-parent-imports -- the launcher composes the modal, which is also the package's standalone public component
import { RovoReintroductionModal } from '../rovo-reintroduction-modal';

import { messages } from './messages';
import { hasBeenDismissed, markDismissed } from './storage';
import { useRovoChatEligibility } from './use-rovo-chat-eligibility';

/**
 * Query param used by the dev-only override below.
 */
const DEV_FORCE_OPEN_PARAM = 'forceRovoReintroductionModal';

/**
 * Dev-only escape hatch for manual testing: append
 * `?forceRovoReintroductionModal=true` to the URL to force the modal open,
 * bypassing the experiment, Rovo entitlement, TAP trait eligibility,
 * localStorage, and the Rovo-chat-open check.
 *
 * Honored ONLY outside production builds (`process.env.NODE_ENV !== 'production'`),
 * so it can never bypass those checks for shipped users — even if the param
 * happens to be present on a production URL.
 */
function isDevForceOpen(): boolean {
	if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') {
		return false;
	}
	try {
		return new URLSearchParams(window.location.search).get(DEV_FORCE_OPEN_PARAM) === 'true';
	} catch {
		return false;
	}
}

/**
 * Public entrypoint. Mounted high in the host product's app shell. Renders
 * nothing unless the user is eligible and in the experiment's test cohort.
 *
 * Audience targeting is resolved client-side from two TAP user-level traits
 * combined with OR: the user has NOT sent a Rovo chat message
 * (`segment_has_sent_rovo_chat_message_aaid` falsy) OR is dormant
 * (`is_rovo_chat_mau` falsy).
 *
 * First-time users are excluded: when a `cloudId` is supplied, anyone still
 * within their first 30 days in the product (per the `<product>_first_active`
 * activation-date trait) is suppressed because they get a dedicated onboarding
 * experience.
 *
 * The experiment is resolved inside the inner component, only once the user is
 * confirmed eligible, so the Statsig exposure event fires only for users who
 * actually qualify for the modal. This means control-cohort users still incur
 * the TAP traits fetch (their cohort cannot be known before the fetch), but the
 * modal never opens for them.
 */
export function RovoReintroductionLauncher(
	props: RovoReintroductionLauncherProps,
): JSX.Element | null {
	return <RovoReintroductionLauncherInner {...props} />;
}

function RovoReintroductionLauncherInner({
	product,
	cloudId,
	userId,
	isRovoEnabled = false,
	openChatMode = 'sidebar',
	prompt,
	testId,
}: RovoReintroductionLauncherProps): JSX.Element | null {
	const devForceOpen = isDevForceOpen();
	const [isOpen, setIsOpen] = useState(devForceOpen);
	const { mode } = useRovoExperienceAPIStore();
	const publishRovo = usePublish('ai-mate');
	const { formatMessage } = useIntl();

	// Suppress if the user is not entitled to Rovo (fail-closed), already shown in
	// this browser, or a Rovo chat is currently open in this session (the traits
	// only reflect past behaviour). When suppressed we also disable the TAP fetch
	// so we never make a needless request.
	const isSuppressed = devForceOpen || !isRovoEnabled || hasBeenDismissed() || mode !== undefined;
	const { loaded, eligible } = useRovoChatEligibility(userId, {
		enabled: !isSuppressed,
		product,
		cloudId,
	});

	// Open at most once per mount, after eligibility resolves. The experiment is
	// only resolved once the user is confirmed eligible, so the Statsig exposure
	// event fires exclusively for users who actually qualify for the modal. The
	// modal then opens only for the experiment's test cohort.
	const hasOpenedRef = useRef(false);
	useEffect(() => {
		if (devForceOpen || hasOpenedRef.current || isSuppressed || !loaded) {
			return;
		}
		hasOpenedRef.current = true;
		if (eligible && expVal(EXPERIMENT_NAME, EXPERIMENT_PARAM, false)) {
			setIsOpen(true);
		}
	}, [devForceOpen, eligible, isSuppressed, loaded]);

	const handleClickPrimary = useCallback(() => {
		// Sending `prompt` without `isPromptPlaceholder` makes Rovo chat auto-send it
		// as the first user message, rather than just opening an empty chat.
		publishRovo({
			type: 'chat-new',
			source: CHAT_OPEN_SOURCE,
			openChat: true,
			openChatMode,
			data: { dialogues: [], prompt: prompt ?? formatMessage(messages.chatPrompt) },
		});
		markDismissed();
		setIsOpen(false);
	}, [formatMessage, openChatMode, prompt, publishRovo]);

	const handleClose = useCallback(() => {
		markDismissed();
		setIsOpen(false);
	}, []);

	if (!isOpen) {
		return null;
	}

	return (
		<RovoReintroductionModal
			analyticsAttributes={{ product }}
			onClickPrimary={handleClickPrimary}
			onClose={handleClose}
			testId={testId}
		/>
	);
}
